import { useCallback, useEffect } from 'react';

import {
  readActiveFiltersFromRouteParams,
  writeRouteParamsFromActiveFilters,
} from './routeFilters.utils';
import type { ActiveFilters, RouteFiltersRouterStore } from './types';

type UseRouteCanonicalParamOptions<
  TFilterKey extends string,
  TValue extends string,
> = {
  routeName: string;
  paramKey: TFilterKey;
  defaultValue: TValue;
  filterKeys: readonly TFilterKey[];
  isKnownFilterKey: (key: string) => key is TFilterKey;
  isValidValue: (value: unknown) => value is TValue;
  routerStore: RouteFiltersRouterStore;
  routeParams?: Record<string, string>;
};

export default function useRouteCanonicalParam<
  TFilterKey extends string,
  TValue extends string,
>({
  routeName,
  paramKey,
  defaultValue,
  filterKeys,
  isKnownFilterKey,
  isValidValue,
  routerStore,
  routeParams = {},
}: UseRouteCanonicalParamOptions<TFilterKey, TValue>) {
  const isParamMissingFromUrl = useCallback(
    (valueFromUrl: unknown) => !isValidValue(valueFromUrl),
    [isValidValue],
  );

  const writeCanonicalParamToUrl = useCallback(
    (value: TValue) => {
      const currentFilters = readActiveFiltersFromRouteParams(
        routeParams,
        filterKeys,
        isKnownFilterKey,
      );

      routerStore.navigate(
        routeName,
        writeRouteParamsFromActiveFilters(
          {
            ...currentFilters,
            [paramKey]: value,
          } as ActiveFilters<TFilterKey>,
          filterKeys,
        ),
        // @ts-ignore router5 navigate options are not in the store types
        { replace: true },
      );
    },
    [
      filterKeys,
      isKnownFilterKey,
      paramKey,
      routeName,
      routeParams,
      routerStore,
    ],
  );

  useEffect(() => {
    const valueFromUrl = routeParams[paramKey];
    const shouldRedirectToDefault = isParamMissingFromUrl(valueFromUrl);

    if (!shouldRedirectToDefault) {
      return;
    }

    writeCanonicalParamToUrl(defaultValue);
  }, [
    defaultValue,
    isParamMissingFromUrl,
    paramKey,
    routeParams,
    writeCanonicalParamToUrl,
  ]);

  const setCanonicalParam = useCallback(
    (value: TValue) => {
      writeCanonicalParamToUrl(value);
    },
    [writeCanonicalParamToUrl],
  );

  return { setCanonicalParam };
}
